<template>
    <div class="screen-wrap">
        <v-app class="hmc-app">
            <v-main class="main-body">
                <v-container class="app-body">
                    <!-- BOC:[error] -->
                    <ApiErrorDialog v-if="worldApi.isError" :api="worldApi" />
                    <ApiErrorDialog v-if="EventApi.isError" :api="EventApi" />
                    <ErrorDialog v-if="error" :error="error" :message="errorMessage" @exitError="exitError" />
                    <!-- EOC -->
                    
                    <!-- Loading indicator while processing -->
                    <div v-if="loading" class="d-flex flex-column align-center justify-center" style="height: 70vh">
                        <v-progress-circular indeterminate color="brown darken-2" size="80"
                            width="8"></v-progress-circular>
                        <div class="text-h5 mt-5">Processing score...</div>
                    </div>
                </v-container>
            </v-main>
        </v-app>
    </div>
</template>

<script>
import { mapState } from "vuex";

export default {
    components: {
        ApiErrorDialog: () =>
            import(
                /* webpackChunkName: "component-api-error-dialog" */ "@/components/ApiErrorDialog.vue"
            ),
        ErrorDialog: () =>
            import(
                /* webpackChunkName: "component-error-dialog" */ "@/components/ErrorDialog.vue"
            ),
    },
    computed: mapState({
        auth: (state) => state.auth.data,
        event: (state) => state.event.data,
        settings: (state) => state.settings.data,
        series: (state) => state.series.data,
        boss: (state) => state.boss.data,
    }),
    data: () => ({
        loading: true,
        error: false,
        errorMessage: "Failed to process score data",
        scoreData: null,
        star: 0,
        score: 0,
        countCorrect: 0,
        event_data: {},
        levelId: null,
        EventApi: {
            isLoading: false,
            isError: false,
            error: null,
            url: null,
        },
        worldApi: {
            isLoading: false,
            isError: false,
            error: null,
            url: null,
        },
        level: {},
        boss_data: null,
    }),
    created() {
        // Check if there's a score token in the URL
        const scoreToken = this.$route.query.scoreToken;

        if (!scoreToken) {
            this.error = true;
            this.errorMessage = "No score token provided";
            this.loading = false;
            return;
        }

        // Fetch the score data using the token
        this.fetchScoreData(scoreToken);
    },
    methods: {
        async fetchScoreData(token) {
            this.worldApi.url = `${process.env.VUE_APP_WORLD_API_URL}/api/game/score/${token}`;
            
            this.worldApi.callbackReset = () => {
                this.worldApi.isLoading = true;
                this.worldApi.isError = false;
                this.worldApi.error = null;
            };
            
            this.worldApi.callbackError = (e) => {
                this.worldApi.isLoading = false;
                this.worldApi.isError = true;
                this.worldApi.error = e;
                this.error = true;
                this.errorMessage = "Failed to fetch score data";
                this.loading = false;
            };
            
            this.worldApi.callbackSuccess = (resp) => {
                this.worldApi.isLoading = false;
                
                if (resp.success && resp.data) {
                    this.scoreData = resp.data;
                    
                    // Extract level ID from the score data
                    const levelId = this.scoreData.game.levelId;
                    
                    // Check if the level ID matches the current one in the store
                    if (this.event.record && this.event.record.levelId === levelId) {
                        // Level ID matches, proceed with processing the score
                        console.log("Level ID match found, processing score");
                        this.levelId = levelId;
                        this.processScoreData();
                        this.loading = false;
                    } else {
                        // Level ID doesn't match, need to fetch level information
                        console.log("Level ID mismatch, fetching level information");
                        this.levelId = levelId;
                        this.fetchLevelInformation(levelId);
                    }
                } else {
                    this.error = true;
                    this.errorMessage = resp.message || "Failed to load score data";
                    this.loading = false;
                }
            };
            
            this.worldApi.method = "GET";
            this.$api.fetch(this.worldApi);
        },

        processScoreData() {
            // Calculate star rating based on the score
            // Assuming we use the same thresholds as in PageEventLevelComplete
            const totalScore = this.scoreData.score.total;

            if (totalScore >= 5000) {
                this.star = 3;
                this.countCorrect = 5;
            } else if (totalScore >= 4000) {
                this.star = 2;
                this.countCorrect = 4;
            } else if (totalScore >= 3000) {
                this.star = 1;
                this.countCorrect = 3;
            } else {
                this.star = 0;
                this.countCorrect = 2;
            }

            this.score = totalScore;

            // If we already had the event data set up with level info, update it
            // Otherwise, updateEventData will handle it directly
            if (this.event.record && this.event.record.levelId === this.scoreData.game.levelId) {
                this.updateEventData();
            }
        },

        updateEventData() {
            // If we had to fetch level info, event_data is already initialized
            // Otherwise, clone from current store
            if (!this.event_data.record) {
                this.event_data = this.$_.cloneDeep(this.event);
            }
            
            // Make sure we have the correct level ID
            if (!this.levelId) {
                this.levelId = this.scoreData.game.levelId;
            }
            
            // Check if boss should be collected (for 3 stars)
            if (this.event_data.boss) {
                this.event_data.boss.isCollected = this.scoreData.score.isBossCollected;
            }

            // Update submission data
            if (this.event_data.submission) {
                this.event_data.submission.countCorrect = this.countCorrect;
                this.event_data.submission.accuracy = this.score / 10;
                this.event_data.submission.star = this.star;
                this.event_data.submission.score = this.score;
                this.event_data.submission.submission += 1;
                this.event_data.submission.levelId = this.levelId;
            }

            // Update record data
            if (this.event_data.record) {
                this.event_data.record.attempt += 1;
                this.event_data.record.submission += 1;

                // Update top score if the current score is higher
                if (this.score > this.event_data.record.topScore) {
                    this.event_data.record.topScore = this.score;
                    this.event_data.record.topStar = this.star;
                }
            }

            // Add random game loot (similar to PageEventLevelComplete)
            this.event_data.gameLoot = Math.floor(Math.random() * (3 - 1 + 1) + 1);

            // Set difficulty
            this.event_data.difficulty = this.scoreData.game.difficulty;

            // Update the store
            this.$store.commit("updateEvent", this.event_data);

            // Redirect to the score page
            this.redirectToScorePage();
        },

        redirectToScorePage() {
            // Short timeout to ensure store is updated
            setTimeout(() => {
                this.$router.push({
                    name: "PageEventGameScore",
                    params: {
                        id: this.levelId,
                    },
                });
            }, 500);
        },

        exitToLevels() {
            this.$router.push({
                name: "PageEventLevels",
            });
        },
        
        exitError() {
            this.error = false;
        },
        
        async fetchLevelInformation(levelId) {
            // Set up the API call similar to PageEventLevelDetail.vue
            this.EventApi.url =
                this.$api.servers.event +
                "/api/v1/" +
                this.$_getLocale() +
                "/game/event/level/" +
                levelId;

            this.EventApi.callbackReset = () => {
                this.EventApi.isLoading = true;
                this.EventApi.isError = false;
                this.EventApi.error = null;
            };

            this.EventApi.callbackError = (e) => {
                this.EventApi.isLoading = false;
                this.EventApi.isError = true;
                this.EventApi.error = e;
                this.error = true;
                this.errorMessage = "Failed to fetch level information";
                this.loading = false;
            };
            
            this.EventApi.callbackSuccess = (resp) => {
                this.level = resp;
                this.EventApi.isLoading = false;
                
                // Find boss data
                this.boss_data = this.boss.find(
                    (element) => element.level_id == levelId
                );
                
                if (!this.boss_data) {
                    console.error("Boss data not found for level ID:", levelId);
                    this.error = true;
                    this.errorMessage = "Boss data not found for this level";
                    this.loading = false;
                    return;
                }
                
                // Now that we have level information, update the event data
                this.updateEventWithLevelInfo();
                
                // Then process the score
                this.processScoreData();
                this.loading = false;
            };
            
            this.EventApi.method = "GET";
            this.$api.fetch(this.EventApi);
        },
        
        updateEventWithLevelInfo() {
            this.event_data = this.$_.cloneDeep(this.event);
            
            // Update record with level information
            this.event_data.record = {
                levelId: this.levelId,
                topStar: this.level.EventRecord ? this.level.EventRecord.topStar : 0,
                topScore: this.level.EventRecord ? this.level.EventRecord.topScore : 0,
                attempt: this.level.EventRecord ? this.level.EventRecord.attempt : 0,
                submission: this.level.EventRecord ? this.level.EventRecord.submission : 0,
                topScoreOld: this.level.EventRecord ? this.level.EventRecord.topScore : 0,
                topStarOld: this.level.EventRecord ? this.level.EventRecord.topStar : 0
            };

            // Update boss information
            this.event_data.boss = {
                key: this.boss_data.boss_key,
                inventory: this.boss_data.inventory,
                img: this.boss_data.boss_img,
                name: this.boss_data.boss_name,
                isCollected: false
            };

            // Set API flags
            this.event_data.api = {
                submission: true,
                record: true,
                player: true,
                inventory: true
            };

            // Initialize submission data
            this.event_data.submission = {
                institutionId: this.auth.Institution ? this.auth.Institution.id : null,
                classroomId: this.auth.Classroom ? this.auth.Classroom.id : null,
                studentId: this.auth.Classroom ? this.auth.User.id : null,
                levelId: this.levelId,
                eventId: this.boss_data.event_id,
                mochiId: this.auth.Player.mochiId,
                syllabusKey: this.level.Level ? this.level.Level.chapterKey.split("_")[0] : "",
                chapterKey: this.level.Level ? this.level.Level.chapterKey : "",
                questionKey: this.level.Level ? this.level.Level.questionKey : "",
                countQuestion: 10,
                countCorrect: 0,
                accuracy: 0,
                time: 0,
                star: 0,
                score: 0,
                submission: this.level.EventRecord ? this.level.EventRecord.submission : 0
            };

            // Update the store with the initial event data
            this.$store.commit("updateEvent", this.event_data);
        },
    },
};
</script>

<style scoped>
.app-title {
    z-index: 99;
    position: fixed;
    top: 0;
    max-width: 480px !important;
    width: 100%;
    display: flex;
    align-items: center;
    height: 60px;
    box-shadow: 0px 5px 2px rgba(128, 128, 128, 0.5) !important;
    border: #caad87 5px solid;
    margin: auto;
    color: #fff;
    background-color: #f7efe3;
    text-shadow: #cbb18f 3px 0px 0px, #cbb18f 2.83487px 0.981584px 0px,
        #cbb18f 2.35766px 1.85511px 0px, #cbb18f 1.62091px 2.52441px 0px,
        #cbb18f 0.705713px 2.91581px 0px, #cbb18f -0.287171px 2.98622px 0px,
        #cbb18f -1.24844px 2.72789px 0px, #cbb18f -2.07227px 2.16926px 0px,
        #cbb18f -2.66798px 1.37182px 0px, #cbb18f -2.96998px 0.42336px 0px,
        #cbb18f -2.94502px -0.571704px 0px, #cbb18f -2.59586px -1.50383px 0px,
        #cbb18f -1.96093px -2.27041px 0px, #cbb18f -1.11013px -2.78704px 0px,
        #cbb18f -0.137119px -2.99686px 0px, #cbb18f 0.850987px -2.87677px 0px,
        #cbb18f 1.74541px -2.43999px 0px, #cbb18f 2.44769px -1.73459px 0px,
        #cbb18f 2.88051px -0.838247px 0px;
}

.app-body {
    max-width: 480px;
    min-height: 100%;
    padding: 80px 0 40px 0 !important;
}

.main-body {
    background-color: rgba(214, 132, 49, 0.3);
}
</style>
